"use client";

import { useMemo } from "react";

import { useAuthState } from "@/hooks/use-auth-state";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { addressesEqual } from "@/utils/address";

import type { WinnerForUI } from "../data/types";
import { getWinnersForUI } from "../data/utils";
import { useCardvaultOverride } from "../test-utils/state-override";
import { useCardvaultCampaign } from "./use-campaign";
import { useCardvaultClaims } from "./use-claimed-weeks";
import { useCardvaultEnrollment } from "./use-enrollment";

export interface CardvaultClaimableWinsResult {
  claimableWins: WinnerForUI[];
  effectiveWinners: WinnerForUI[];
  accountAddress: string | undefined;
  isWalletConnected: boolean;
  isLoading: boolean;
}

/**
 * Single source of truth for "what wins can this wallet still claim".
 *
 * Combines:
 *  - Static winners pulled from Firestore-backed campaign (GET /api/card-vault/campaign)
 *  - Synthetic winners from ?cv_state= override (dev only)
 *  - Already-claimed weeks from Firestore (GET /api/card-vault/claims/:owner)
 *
 * Used by both the page-level claim wizard and the global claim banner.
 */
export function useCardvaultClaimableWins(): CardvaultClaimableWinsResult {
  const { accountAddress: authAccountAddress } = useAuthState();
  const {
    connected,
    accountAddress: walletAccountAddress,
    ethAddress,
    solanaAddress,
  } = useDecibelWallet();
  const { enrolledSubaccount } = useCardvaultEnrollment();
  const override = useCardvaultOverride();
  const { campaign, isLoading } = useCardvaultCampaign();

  // `authAccountAddress` is set for both WALLET_CONNECTED and
  // SESSION_KEY_IMPORTED states, so it's the right signal for "user is
  // authenticated to the dex" — `isWalletConnected` would exclude users who
  // arrived via a mobile-from-desktop session-key link.
  const accountAddress =
    authAccountAddress ?? (override && connected ? walletAccountAddress : undefined);
  const effectiveIsWalletConnected = !!authAccountAddress || Boolean(override && connected);

  const { data: claimsData } = useCardvaultClaims(accountAddress);

  const baseWinners = useMemo<WinnerForUI[]>(
    () => (campaign ? getWinnersForUI(campaign.weeks) : []),
    [campaign],
  );

  // Whatever chain wallet the user recognizes as "their wallet".
  const externalWallet = ethAddress ?? solanaAddress ?? undefined;

  const overrideWins = override?.wins;
  const effectiveWinners = useMemo<WinnerForUI[]>(() => {
    if (!campaign || !overrideWins?.length || !accountAddress) return baseWinners;
    const synthetic = overrideWins
      .map((week): WinnerForUI | null => {
        // If the connected wallet is already a real winner for any card in
        // this week, skip synthesis — the real Firestore entry is more
        // accurate (claim_status, external wallet, subaccount).
        const alreadyReal = baseWinners.some(
          (w) => w.week === week && addressesEqual(w.wallet, accountAddress),
        );
        if (alreadyReal) return null;
        const weekEntry = campaign.weeks.find((w) => w.week === week);
        const card = weekEntry?.cards[0];
        if (!weekEntry || !card) return null;
        return {
          week,
          cardName: `${card.pokemon} ${card.set}`,
          cardImage: card.image_url,
          grade: `${card.grader.toUpperCase()} ${card.grade}`,
          gradeType: (card.grader.toLowerCase() || "psa") as "psa" | "cgc",
          wallet: accountAddress,
          externalWallet: externalWallet ?? accountAddress,
          subaccount: enrolledSubaccount,
          tickets: 1,
          status: "pending",
        };
      })
      .filter((w): w is WinnerForUI => w !== null);
    // Synthetic winners are prepended; real winners stay so the Hall of
    // Fame remains correct for the other 6 cards in the week.
    return [...synthetic, ...baseWinners];
  }, [campaign, overrideWins, accountAddress, baseWinners, externalWallet, enrolledSubaccount]);

  const effectiveClaimedWeeks = useMemo(
    () => override?.claimed ?? claimsData?.claimedWeeks ?? [],
    [override?.claimed, claimsData?.claimedWeeks],
  );

  const claimableWins = useMemo(() => {
    if (!effectiveIsWalletConnected || !accountAddress) return [];
    const claimedWeeks = new Set(effectiveClaimedWeeks);
    return effectiveWinners.filter(
      (w) => addressesEqual(w.wallet, accountAddress) && !claimedWeeks.has(w.week),
    );
  }, [effectiveIsWalletConnected, accountAddress, effectiveClaimedWeeks, effectiveWinners]);

  return {
    claimableWins,
    effectiveWinners,
    accountAddress,
    isWalletConnected: effectiveIsWalletConnected,
    isLoading,
  };
}
